import * as Sentry from '@sentry/react'

const GOOGLE_MAPS_AUTH_ERROR_PATTERN =
  /AuthFailure|(?:ApiNotActivatedMapError|ApiTargetBlockedMapError|BillingNotEnabledMapError|ClientBillingNotEnabledMapError|ExpiredKeyMapError|InvalidKeyMapError|ProjectDeniedMapError|RefererNotAllowedMapError|UnauthorizedURLForClientIdMapError)|Google Maps.*(?:authentication|API key)/i

const INSTALLATION_FLAG = '__stoplightGoogleMapsSentryMonitoringInstalled'

declare global {
  interface Window {
    gm_authFailure?: () => void
    [INSTALLATION_FLAG]?: boolean
  }
}

function getOrigin(value: string): string | null {
  if (!value) return null

  try {
    return new URL(value).origin
  } catch {
    return null
  }
}

function getGoogleMapsContext(source: string) {
  return {
    source,
    origin: window.location.origin,
    path: window.location.pathname,
    referrerOrigin: getOrigin(document.referrer),
    online: navigator.onLine,
    language: navigator.language,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    mapApiKeyPresent: Boolean(import.meta.env.VITE_MAP_API_KEY),
    userAgent: navigator.userAgent,
  }
}

function captureGoogleMapsAuthFailure(source: string): void {
  const error = new Error('Google Maps JavaScript API authentication failed')
  error.name = 'GoogleMapsAuthFailure'

  Sentry.captureException(error, {
    tags: {
      integration: 'google-maps',
      google_maps_error: 'AuthFailure',
      google_maps_source: source,
    },
    contexts: {
      google_maps: getGoogleMapsContext(source),
    },
    fingerprint: ['google-maps-auth-failure'],
  })

  // Give the browser transport a chance to send the event immediately. This is
  // intentionally fire-and-forget so a Sentry outage never blocks the app.
  void Sentry.flush(2000)
}

function getEventText(event: ErrorEvent | PromiseRejectionEvent): string {
  const reason = 'reason' in event ? event.reason : event.error

  return [
    'message' in event ? event.message : '',
    reason instanceof Error ? reason.message : String(reason ?? ''),
  ].join(' ')
}

function installGoogleMapsAuthFailureHook(): void {
  const descriptor = Object.getOwnPropertyDescriptor(window, 'gm_authFailure')

  // @vis.gl/react-google-maps assigns gm_authFailure while it starts loading
  // the API. Intercept that assignment so the library's own status handling is
  // preserved and Sentry is notified whenever Google invokes the callback.
  if (!descriptor || descriptor.configurable) {
    let currentCallback =
      typeof window.gm_authFailure === 'function'
        ? window.gm_authFailure
        : undefined

    let wrappedCallback: (() => void) | undefined
    const setCallback = (callback: unknown) => {
      currentCallback =
        typeof callback === 'function' ? (callback as () => void) : undefined
      wrappedCallback = currentCallback
        ? () => {
            try {
              currentCallback?.()
            } finally {
              captureGoogleMapsAuthFailure('gm_authFailure')
            }
          }
        : undefined
    }

    Object.defineProperty(window, 'gm_authFailure', {
      configurable: true,
      enumerable: descriptor?.enumerable ?? true,
      get: () => wrappedCallback,
      set: setCallback,
    })

    setCallback(currentCallback)
  }
}

function installGoogleMapsErrorListeners(): void {
  window.addEventListener(
    'error',
    event => {
      const text = getEventText(event)
      if (GOOGLE_MAPS_AUTH_ERROR_PATTERN.test(text)) {
        captureGoogleMapsAuthFailure('window_error')
      }
    },
    true,
  )

  window.addEventListener('unhandledrejection', event => {
    const text = getEventText(event)
    if (GOOGLE_MAPS_AUTH_ERROR_PATTERN.test(text)) {
      captureGoogleMapsAuthFailure('unhandledrejection')
    }
  })
}

/**
 * Installs browser-level monitoring before APIProvider loads Google Maps.
 * The callback is intentionally independent of map rendering, so it also
 * captures failures that only show the AuthFailure fallback UI.
 */
export function installGoogleMapsSentryMonitoring(): void {
  if (window[INSTALLATION_FLAG]) return

  window[INSTALLATION_FLAG] = true
  installGoogleMapsAuthFailureHook()
  installGoogleMapsErrorListeners()
}
